import _ from "lodash";

import { IStripeSubscriptionState } from "../../IStripeSubscriptionState";
import { IWorkspacePlan } from "../../IWorkspacePlan";
import { IGenericModelFields } from "../IGenericFields";

export enum WorkspaceUserRole {
    OWNER = "owner",
    MEMBER = "member",
    LIBRARIAN = "librarian",
    GUEST = "guest"
}

export enum WorkspaceIntegrationType {
    BRANDFOLDER = "brandfolder",
    SALESFORCE = "salesforce",
}

export interface IWorkspacePlayerSettings {
    showTitle: boolean,
    showAuthor: boolean,
    requireEmail: boolean,
    showGetDeckUI: boolean,
    allowPdfDownload: boolean,
    customPlayerURLTemplate: string | null,
}

export interface IWorkspacePlayerBranding {
    active: boolean;
    backgroundColor: string;
    logoId: string | null;
    logoScale: number;
    useLoadingAnimation: boolean;
}

export interface IWorkspaceSSOSettings {
    enabled: boolean;
    strict: boolean;
    allowIdpInitiated: boolean;
    samlConfig?: {
        audience?: string;
        callbackUrl?: string;
        issuer?: string;
        certs: string[];
        entryPoint: string;
        idpIssuer: string;
    };
    userProvisioning: {
        allowJit: boolean;
        allowScim: boolean;
        scimToken?: string;
    };
}

export interface IWorkspaceStripe {
    customerId?: string;
    subscriptionId?: string;
}

export interface IWorkspaceIntegrationConfig {
    enabled: boolean;
}

export interface IWorkspaceSalesforceIntegrationConfig extends IWorkspaceIntegrationConfig {
    domainName: string;
    prn: string;
}

export interface IWorkspaceDomain {
    domain: string;
    verified: boolean;
    verificationKey: string;
    verificationError: string;
    verifiedAt: number;
}

export interface IWorkspaceStoredMetadata {
    /**
     * When true, any authenticated user can read workspace resources without membership.
     * Used for catalog workspaces (e.g. presentation templates).
     */
    isPublic?: boolean;
}

export interface IWorkspace extends IGenericModelFields {
    name: string;
    photoURL?: string | null;
    stripe: IWorkspaceStripe;
    sso: IWorkspaceSSOSettings;
    player: {
        settings: IWorkspacePlayerSettings;
        branding: IWorkspacePlayerBranding;
    },
    integrations: {
        [WorkspaceIntegrationType.BRANDFOLDER]?: IWorkspaceIntegrationConfig;
        [WorkspaceIntegrationType.SALESFORCE]?: IWorkspaceSalesforceIntegrationConfig;
    };
    // Legacy, will be removed
    domain?: IWorkspaceDomain;
    // Current
    domains: IWorkspaceDomain[];
    metadata?: IWorkspaceStoredMetadata;
    partnerStackClickId?: string;
    partnerStackPartnerKey?: string;
}

export type ISanitizedWorkspace = Omit<IWorkspace, "stripe" | "sso">;

export interface IWorkspaceDocumentChangedEvent<T = any> {
    timestamp: number;
    documentType: "Workspace" | "WorkspaceUser" | "WorkspaceUserGroup" | "WorkspacePermission" | "WorkspaceResource" | "WorkspaceResourceFolder" | "WorkspacePlan" | "WorkspaceUserInvite";
    documentId: string;
    operationType: "create" | "update" | "delete";
    oldDocument: T | null;
    newDocument: T | null;
}

export interface IWorkspacePlanMetadata {
    plan: IWorkspacePlan;
    subscriptionState: Pick<IStripeSubscriptionState, "type" | "subscriptionStatus"> | null;
    subscriptionChargeRequiresAction: boolean;
    subscriptionCanceledAt: number | null;
    subscriptionWasVoluntarilyCanceled: boolean;
    subscriptionScheduledToCancelAt: number | null;
    subscriptionStripeProductName: string | null;
    forceLockWorkspace: boolean;
    accountManagerEmail: string | null;
    subscriptionTrialEndsAt: number | null;
    subscriptionHadTrial: boolean;
    subscriptionPaidCycles: number | null;
    subscriptionLatestInvoicePaymentAttempts: number;
    subsctiptionTrialWasCanceled: boolean;
    subscriptionTrialFailed: boolean;
}

export interface IWorkspaceMetadata extends IWorkspacePlanMetadata {
    id: string;
    name: string;
    createdAt: number;
    userRole: WorkspaceUserRole;
    workspacePhotoURL: string | null;
    counters: {
        users: { [key in WorkspaceUserRole]: number };
        userInvites: number;
    }
}

////////////////////////////// DEFAULTS //////////////////////////////

// Currently defined in features.js
const DEFAULT_WORKSPACE_PLAYER_SETTINGS: IWorkspacePlayerSettings = {
    showTitle: true,
    showAuthor: true,
    requireEmail: false,
    showGetDeckUI: false,
    allowPdfDownload: false,
    customPlayerURLTemplate: null,
};

// Currently defined in features.js
const DEFAULT_WORKSPACE_PLAYER_BRANDING: IWorkspacePlayerBranding = {
    active: false,
    backgroundColor: "#000000",
    logoId: null,
    logoScale: 1,
    useLoadingAnimation: true,
};

const DEFAULT_WORKSPACE_SSO_SETTINGS: IWorkspaceSSOSettings = {
    enabled: false,
    strict: false,
    allowIdpInitiated: true,
    userProvisioning: {
        allowJit: false,
        allowScim: false,
    },
};

export const GET_DEFAULT_WORKSPACE_PLAYER_AND_SSO_SETTINGS = () => _.cloneDeep<Pick<IWorkspace, "player" | "sso">>({
    player: {
        settings: DEFAULT_WORKSPACE_PLAYER_SETTINGS,
        branding: DEFAULT_WORKSPACE_PLAYER_BRANDING,
    },
    sso: DEFAULT_WORKSPACE_SSO_SETTINGS,
});

const DEFAULT_WORKSPACE_METADATA_USERS_COUNTER: IWorkspaceMetadata["counters"]["users"] = Object.values(WorkspaceUserRole).reduce((acc, role) => ({ ...acc, [role]: 0 }), {} as { [key in WorkspaceUserRole]: number });

export const GET_DEFAULT_WORKSPACE_METADATA_USERS_COUNTER = () => _.cloneDeep(DEFAULT_WORKSPACE_METADATA_USERS_COUNTER);
